import { Component, computed, input } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  DEFAULT_CHIP_LABEL_MAX_LENGTH,
  truncateChipLabel,
} from '../../../utils/chip-label.utils';

/** A single chip: a label coloured by whether it is a primary or secondary membership. */
export interface MembershipChipItem {
  label: string;
  isPrimary: boolean;
}

/** A chip prepared for rendering: its label is truncated with an ellipsis when
 *  longer than the limit, with the full text surfaced via tooltip when truncated. */
interface DisplayChip {
  /** Label as shown (truncated with an ellipsis when over the limit). */
  display: string;
  /** Full, untruncated label — shown in a tooltip when truncated. */
  full: string;
  isPrimary: boolean;
  truncated: boolean;
}

/**
 * The single chip-list component for practice-area and office displays across the
 * app (Resource Management table, resources directory, dashboard, requests,
 * assignments, detail pages). Renders a compact, wrapping list of chips, rolling
 * everything past `maxVisible` into a grey "+N" chip (full list on hover). Long
 * labels are truncated past `maxLabelLength`, with the full text on hover.
 *
 * Accepts strings or arbitrary objects (label read via `labelKey`, default 'label').
 * Chips render in the primary (blue) colour by default; set `differentiatePrimary`
 * to colour by each item's `isPrimary` (blue primary / grey secondary) — only useful
 * for memberships that carry that distinction (e.g. primary vs secondary office).
 */
@Component({
  selector: 'app-membership-chips',
  standalone: true,
  imports: [MatTooltipModule],
  templateUrl: './membership-chips.component.html',
})
export class MembershipChipsComponent {
  readonly items = input<readonly (string | object)[]>([]);
  /** Key to read each chip's label from when items are objects (default 'label'). */
  readonly labelKey = input<string>('label');
  readonly maxVisible = input<number>(2);
  /** Chip labels longer than this are truncated with an ellipsis (full text in a tooltip). */
  readonly maxLabelLength = input<number>(DEFAULT_CHIP_LABEL_MAX_LENGTH);
  /**
   * When true, chips are coloured by `isPrimary` (blue primary / grey secondary) —
   * for memberships that carry that distinction (e.g. primary vs secondary office).
   * When false (default), the distinction is ignored and every chip uses the primary
   * (blue) style — for flat lists like practice areas with no primary/secondary concept.
   */
  readonly differentiatePrimary = input<boolean>(false);

  private readonly normalized = computed<MembershipChipItem[]>(() => {
    const key = this.labelKey();
    return (this.items() ?? [])
      .map((item): MembershipChipItem | null => {
        if (item == null) return null;
        if (typeof item === 'string') return { label: item, isPrimary: true };
        const record = item as Record<string, unknown>;
        const rawLabel = record[key] ?? record['label'];
        const label = rawLabel == null ? '' : String(rawLabel);
        // Default to the primary (blue) colour unless the item is explicitly secondary.
        return { label, isPrimary: record['isPrimary'] !== false };
      })
      .filter(
        (chip): chip is MembershipChipItem => !!chip && chip.label.length > 0,
      );
  });

  readonly visible = computed<DisplayChip[]>(() => {
    const max = this.maxLabelLength();
    return this.normalized()
      .slice(0, this.maxVisible())
      .map((chip) => {
        const display = truncateChipLabel(chip.label, max);
        return {
          full: chip.label,
          display,
          isPrimary: chip.isPrimary,
          truncated: display !== chip.label,
        };
      });
  });

  readonly hidden = computed(() => this.normalized().slice(this.maxVisible()));
  readonly hiddenCount = computed(() => this.hidden().length);
  readonly tooltipText = computed(() =>
    this.hidden()
      .map((item) => item.label)
      .join('\n'),
  );
  readonly hasAny = computed(() => this.normalized().length > 0);
}
